import MainTitle from "../../../components/ui/MainTitle/MainTitle";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useBlocker, useParams } from "react-router-dom";
import { Form, FormInstance, Modal } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { useGetLegalPageContentQuery } from "../../../api/queries";
import { useLegalPageMutations } from "../../../api/mutations";
import { QUERY_CONSTANTS } from "../../../api/queryKeys";
import { LEGAL_PAGES, LegalPageKey, LegalPageLanguageType } from "../../../models/LegalPage";
import { LegalPageService } from "../../../services/legal-page.service";
import RichTextEditor from "../../../components/ui/RichTextEditor/RichTextEditor";
import CustomButton from "../../../components/ui/CustomButton/CustomButton";
import FormItemSelect from "../../../components/ui/FormItemSelect/FormItemSelect";
import ConfirmPopup from "../../../components/ConfirmPopup/ConfirmPopup";
import Loader from "../../../components/Loader/Loader";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { openSuccessMessage } from "../../../store/message/messageSlice";
import { handleError } from "../../../helper/errorHandler";
import { convertQuillToInlineStyles } from "../../../helper/convertQuillToInlineStyles";

// Same as on the Automated Emails page
const languageSelectItems: { id: LegalPageLanguageType; label: string }[] = [
  { id: "en", label: "English" },
  { id: "el", label: "Greek" },
];

// An editor with nothing typed in still holds markup like <p><br></p>
const isEmptyText = (html: string) =>
  !html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, "")
    .trim();

const EditLegalPage = () => {
  const { key } = useParams();
  const page = LEGAL_PAGES[key as LegalPageKey];
  if (!page) return <Navigate to={"/terms-and-policies"} replace />;
  return <LegalPageEditor pageKey={key as LegalPageKey} labelKey={page.labelKey} />;
};

const LegalPageEditor: React.FC<{ pageKey: LegalPageKey; labelKey: string }> = ({
  pageKey,
  labelKey,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { saveLegalPage, isSaving } = useLegalPageMutations();
  const formRef = useRef<FormInstance>(null);

  const [language, setLanguage] = useState<LegalPageLanguageType>("en");
  const { data: loaded, isLoading } = useGetLegalPageContentQuery(pageKey, language);
  const [content, setContent] = useState("");
  // The text as it is on the server, in the form Quill gives it back
  const [saved, setSaved] = useState("");
  // Quill rewrites a loaded text into its own markup by itself - that version is the saved one too
  const takeNormalizedAsSaved = useRef(false);
  const hasUnsavedChanges = content !== saved;
  // A language picked while there are unsaved changes - waits for the confirm
  const [pendingLanguage, setPendingLanguage] = useState<LegalPageLanguageType | null>(null);

  useEffect(() => {
    if (loaded === undefined) return;
    takeNormalizedAsSaved.current = true;
    setContent(loaded ?? "");
    setSaved(loaded ?? "");
    // language too: two languages without a text both load null
  }, [loaded, language]);

  const onEditorChange = (html: string, isUserChange: boolean) => {
    setContent(html);
    if (isUserChange) takeNormalizedAsSaved.current = false;
    else if (takeNormalizedAsSaved.current) setSaved(html);
  };

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
  );

  // Reload or closing the tab: only the browser's own dialog can be shown there
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsavedChanges]);

  const copySource: LegalPageLanguageType = language === "en" ? "el" : "en";
  const copyTexts =
    copySource === "en"
      ? { button: t("CopyFromEnglish"), confirm: t("CopyPageFromEnglishConfirm") }
      : { button: t("CopyFromGreek"), confirm: t("CopyPageFromGreekConfirm") };

  const copyFromOtherLanguage = () => {
    Modal.confirm({
      title: copyTexts.button,
      content: copyTexts.confirm,
      okText: t("Confirm"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          const res = await LegalPageService.getContent(pageKey, copySource);
          // Not saved yet, so it stays an unsaved change
          takeNormalizedAsSaved.current = false;
          setContent(res.data.content ?? "");
        } catch (e) {
          handleError(e, dispatch, t);
        }
      },
    });
  };

  const save = () => {
    if (isSaving) return;
    const html = isEmptyText(content) ? "" : convertQuillToInlineStyles(content);
    saveLegalPage(
      { key: pageKey, language, content: html },
      {
        onSuccess: () => {
          dispatch(openSuccessMessage(t("PageSavedSuccessfully")));
          setSaved(content);
          queryClient.invalidateQueries({ queryKey: [QUERY_CONSTANTS.GET_ALL_LEGAL_PAGES] });
          queryClient.removeQueries({
            queryKey: [QUERY_CONSTANTS.GET_LEGAL_PAGE_CONTENT, pageKey, language],
          });
        },
        onError: (e) => handleError(e, dispatch, t),
      }
    );
  };

  const selectLanguage = (value: LegalPageLanguageType) => {
    // The other language loads its own text, so the changes here would be lost
    if (hasUnsavedChanges) {
      formRef.current?.setFieldsValue({ language });
      setPendingLanguage(value);
      return;
    }
    setLanguage(value);
  };

  return (
    <div className={"page-content"}>
      <MainTitle text={t(labelKey)} />

      <Form
        className={"form"}
        ref={formRef}
        name="edit-legal-page"
        initialValues={{ language: "en" }}
        autoComplete="off"
        layout={"vertical"}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
          <FormItemSelect
            name={"language"}
            label={t("Language")}
            rules={{ required: true }}
            items={languageSelectItems}
            onChange={(value) => selectLanguage(value as LegalPageLanguageType)}
          />
        </div>
      </Form>

      <CustomButton
        text={copyTexts.button}
        type={"button"}
        typeStyle={"outline"}
        style={{ width: "auto", marginBottom: "16px" }}
        functionOnClick={copyFromOtherLanguage}
      />

      {isLoading ? (
        <Loader />
      ) : (
        <RichTextEditor
          value={content}
          setValue={onEditorChange}
          style={{ width: "100%", height: "500px" }}
        />
      )}

      <CustomButton
        text={t("SaveChanges")}
        type={"button"}
        typeStyle={"fill"}
        style={{ width: "400px", marginTop: "65px" }}
        functionOnClick={save}
      />

      {blocker.state === "blocked" && (
        <ConfirmPopup
          title={t("UnsavedChangesLeavePage")}
          onClickYes={() => blocker.proceed()}
          onClickNo={() => blocker.reset()}
        />
      )}

      {pendingLanguage && (
        <ConfirmPopup
          title={t("UnsavedChangesLeavePage")}
          onClickYes={() => {
            formRef.current?.setFieldsValue({ language: pendingLanguage });
            setLanguage(pendingLanguage);
            setPendingLanguage(null);
          }}
          onClickNo={() => setPendingLanguage(null)}
        />
      )}
    </div>
  );
};

export default EditLegalPage;
